import React, { useMemo, useState } from "react";
import { Button, Badge, Card, Col, Modal, Row, Table } from "@themesberg/react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import ImagePreview from "../partials/image-preview/ImagePreview";

const formatDateTime = (value) => {
  if (!value) return "-";
  const m = moment(value);
  if (!m.isValid()) return String(value);
  return m.format("DD/MM/YYYY HH:mm");
};

const formatVnd = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toLocaleString("vi-VN") + "đ";
};

const getStatusInfo = (status) => {
  if ([1, 2].includes(status)) {
    return { text: "Đơn mới", color: "warning" };
  } else if ([3, 4, 5].includes(status)) {
    return { text: "Hoàn thành", color: "success" };
  }
  return { text: "Không xác định", color: "secondary" };
};

const getRegionLabel = (region) => {
  switch (region) {
    case "MN":
      return "Miền Nam";
    case "MT":
      return "Miền Trung";
    case "MB":
      return "Miền Bắc";
    default:
      return region || "-";
  }
};

const getTicketStatusInfo = (status) => {
  switch (Number(status)) {
    case 1:
      return { text: "Đã mua", color: "info" };
    case 2:
      return { text: "Đang chụp", color: "warning" };
    case 3:
      return { text: "Đã xuất vé", color: "primary" };
    case 4:
      return { text: "Trúng thưởng", color: "success" };
    case 5:
      return { text: "Không trúng", color: "secondary" };
    default:
      return { text: "Hủy", color: "danger" };
  }
};

const formatNumbers = (value) => {
  if (!value) return "-";
  return String(value).split("").join(" ");
};

const groupTicketsByChannel = (tickets = []) => {
  const map = new Map();
  for (const ticket of tickets || []) {
    const channelName = ticket?.channelName || ticket?.ClassicLotteryChannel?.name || ticket?.channelId || "N/A";
    const amount = Number(ticket?.amount || 0);
    map.set(channelName, (map.get(channelName) || 0) + (Number.isFinite(amount) ? amount : 0));
  }
  return Array.from(map.entries())
    .map(([channel, amount]) => ({ channel, amount }))
    .sort((a, b) => b.amount - a.amount);
};

const resolveWinningSummary = (tickets = []) => {
  if (!Array.isArray(tickets) || tickets.length === 0) return null;

  const totalReward = tickets.reduce((sum, t) => {
    const rewardValue = Number(t?.rewardAmount || t?.reward || 0);
    return sum + (Number.isFinite(rewardValue) ? rewardValue : 0);
  }, 0);

  const wonAmount = tickets.reduce((sum, t) => {
    if (Number(t?.status) !== 4) return sum;
    return sum + Number(t?.amount || 0);
  }, 0);

  const notWonAmount = tickets.reduce((sum, t) => {
    if (Number(t?.status) !== 5) return sum;
    return sum + Number(t?.amount || 0);
  }, 0);

  const pendingAmount = tickets.reduce((sum, t) => {
    if ([4, 5].includes(Number(t?.status))) return sum;
    return sum + Number(t?.amount || 0);
  }, 0);

  return { totalReward, wonAmount, notWonAmount, pendingAmount };
};

export default function OrderDetailModal({ show, onHide, order }) {
  const [imagePreviewShow, setImagePreviewShow] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedImageTitle, setSelectedImageTitle] = useState("");
  const [brokenTicketImageIds, setBrokenTicketImageIds] = useState(() => new Set());

  const showImagePreview = (title, image) => {
    setSelectedImageTitle(title);
    setSelectedImage(image);
    setImagePreviewShow(true);
  };

  const viewModel = useMemo(() => {
    if (!order) return null;

    const statusInfo = getStatusInfo(order.status);
    const ticketByChannels = groupTicketsByChannel(order.tickets);
    const winningSummary = resolveWinningSummary(order.tickets);
    const totalTickets = (order.tickets || []).reduce((sum, t) => sum + Number(t?.amount || 0), 0);

    return {
      id: order.id,
      region: getRegionLabel(order.region),
      status: statusInfo,
      totalValue: order.totalValue,
      createdAt: formatDateTime(order.createdAt),
      doneAt: formatDateTime(order.doneAt || order.modified),
      user: order.user,
      tickets: order.tickets || [],
      ticketByChannels,
      winningSummary,
      totalTickets,
      images: order.images || [],
    };
  }, [order]);

  const linkTo = viewModel ? `/classic/classic/search?search=${viewModel.id}` : "#";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      aria-labelledby="order-detail-modal-title"
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title id="order-detail-modal-title">
          Chi tiết đơn hàng {viewModel?.id ? `#${viewModel.id}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!order || !viewModel ? (
          <div className="text-muted text-center py-5">Không có dữ liệu để hiển thị</div>
        ) : (
          <Row className="g-3">
            <Col md={6}>
              <Card border="light" className="shadow-sm h-100">
                <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                  <span className="fw-bold">Thông tin đơn hàng</span>
                  <Badge bg={viewModel.status.color} className="px-3 py-2">
                    {viewModel.status.text}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <Table responsive size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: 140 }}>Mã đơn</td>
                        <td className="fw-bold text-primary">{viewModel.id}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Vùng miền</td>
                        <td><Badge bg="info">{viewModel.region}</Badge></td>
                      </tr>
                      <tr>
                        <td className="text-muted">Tổng tiền</td>
                        <td className="fw-bold text-success">{formatVnd(viewModel.totalValue)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Số lượng vé</td>
                        <td className="fw-bold">{viewModel.totalTickets}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Ngày tạo</td>
                        <td>{viewModel.createdAt}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Ngày xuất</td>
                        <td>{viewModel.doneAt}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card border="light" className="shadow-sm h-100">
                <Card.Header className="bg-light fw-bold">Người chơi</Card.Header>
                <Card.Body>
                  <Table responsive size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: 140 }}>Tên</td>
                        <td className="fw-bold">{viewModel.user?.name || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Số điện thoại</td>
                        <td className="fw-bold text-primary">{viewModel.user?.phone || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Loại user</td>
                        <td>
                          <Badge bg={viewModel.user?.userType === 0 ? "success" : "warning"}>
                            {viewModel.user?.userType === 0 ? "Online" : "Bán quầy"}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card border="light" className="shadow-sm h-100">
                <Card.Header className="bg-light fw-bold">Chi tiết vé theo đài</Card.Header>
                <Card.Body style={{ maxHeight: 200, overflowY: "auto" }}>
                  {viewModel.ticketByChannels.length > 0 ? (
                    <Table responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Đài</th>
                          <th className="text-end">Số lượng</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewModel.ticketByChannels.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.channel}</td>
                            <td className="text-end fw-bold">{item.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted text-center">Không có dữ liệu</div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card border="light" className="shadow-sm h-100">
                <Card.Header className="bg-light fw-bold">Kết quả trúng thưởng</Card.Header>
                <Card.Body>
                  {viewModel.winningSummary ? (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Trúng:</span>
                        <Badge bg="success" className="px-3 py-2">{viewModel.winningSummary.wonAmount} vé</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Không trúng:</span>
                        <Badge bg="danger" className="px-3 py-2">{viewModel.winningSummary.notWonAmount} vé</Badge>
                      </div>
                      {viewModel.winningSummary.pendingAmount > 0 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span>Chờ kết quả:</span>
                          <Badge bg="warning" text="dark" className="px-3 py-2">{viewModel.winningSummary.pendingAmount} vé</Badge>
                        </div>
                      )}
                      {viewModel.winningSummary.totalReward > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                          <span className="fw-bold">Tổng tiền trúng:</span>
                          <span className="fw-bold text-success fs-5">{formatVnd(viewModel.winningSummary.totalReward)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted text-center">Chưa có kết quả</div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={12}>
              <Card border="light" className="shadow-sm">
                <Card.Header className="bg-light fw-bold">Danh sách vé</Card.Header>
                <Card.Body style={{ maxHeight: 320, overflowY: "auto" }}>
                  {viewModel.tickets.length > 0 ? (
                    <Table responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: 60 }}>#</th>
                          <th>Đài</th>
                          <th>Số</th>
                          <th className="text-center">SL</th>
                          <th className="text-center" style={{ width: 90 }}>Ảnh</th>
                          <th className="text-center">Trạng thái</th>
                          <th className="text-end">Tiền trúng</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewModel.tickets.map((ticket, idx) => {
                          const channelName =
                            ticket?.channelName ||
                            ticket?.ClassicLotteryChannel?.name ||
                            ticket?.channelId ||
                            "-";
                          const channelLogo = ticket?.ClassicLotteryChannel?.logo;
                          const numbers =
                            ticket?.ClassicLotteryStorage?.numbers || ticket?.numbers || "";
                          const statusInfo = getTicketStatusInfo(ticket?.status);
                          const rewardValue =
                            ticket?.rewardAmount || ticket?.reward || ticket?.rewardValue || 0;
                          const ticketImageUrl = ticket?.ClassicLotteryStorage?.images || ticket?.images || "";
                          const ticketId = String(ticket?.id || ticket?.ticketId || idx);
                          const hasBrokenImage = brokenTicketImageIds.has(ticketId);
                          const canShowImage = Boolean(ticketImageUrl) && !hasBrokenImage;

                          return (
                            <tr key={`${ticket?.id || ticket?.ticketId || idx}-${idx}`}>
                              <td className="text-center fw-bold">{idx + 1}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {channelLogo ? (
                                    <img
                                      src={channelLogo}
                                      alt={channelName}
                                      className="rounded border border-gray-200"
                                      style={{ width: 32, height: 32, objectFit: "cover" }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : null}
                                  <span className="fw-semibold">{channelName}</span>
                                </div>
                              </td>
                              <td className="fw-bold text-primary">{formatNumbers(numbers)}</td>
                              <td className="text-center">{ticket?.amount ?? "-"}</td>
                              <td className="text-center">
                                {canShowImage ? (
                                  <div
                                    className="rounded border overflow-hidden d-inline-block shadow-sm"
                                    style={{ width: 60, height: 60, cursor: "pointer" }}
                                    onClick={() => showImagePreview(`Ảnh vé #${idx + 1}`, ticketImageUrl)}
                                  >
                                    <img
                                      src={ticketImageUrl}
                                      alt={`Ảnh vé #${idx + 1}`}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      onError={() => {
                                        setBrokenTicketImageIds((prev) => {
                                          const next = new Set(prev);
                                          next.add(ticketId);
                                          return next;
                                        });
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <span className="text-muted small">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Badge bg={statusInfo.color} className="px-3 py-2">
                                  {statusInfo.text}
                                </Badge>
                              </td>
                              <td className="text-end fw-bold text-success">
                                {Number(rewardValue) > 0 ? formatVnd(rewardValue) : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted text-center">Không có vé</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Link to={linkTo} target="_blank" style={{ textDecoration: "none" }}>
          <Button variant="primary">
            Xem danh sách vé
            <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-2" />
          </Button>
        </Link>
        <Button variant="secondary" onClick={onHide}>
          Đóng
        </Button>
      </Modal.Footer>

      <ImagePreview
        show={imagePreviewShow}
        title={selectedImageTitle}
        image={selectedImage}
        onHide={() => setImagePreviewShow(false)}
      />
    </Modal>
  );
}
